import React, { PureComponent, ReactNode } from 'react';
import { FieldRenderProps } from 'react-final-form';
import styled, { css } from 'styled-components';
import { FormField, Label, Box, Text, FieldError } from 'mdlkit';
import { FormattedMessage } from 'react-intl';
import {
  maxLength,
  minLength,
  required,
  requiredNumber,
  requiredUppercase,
  requiredLowercase,
  requiredSpecialCharacter,
} from '../../../utils/shared/validations';
import { INVALID_PASSWORD } from '../../../constants/errors';

const StyledBox = styled(Box)`
  position: relative;
  width: 100%;
`;

const PasswordError = styled(FieldError)`
  position: absolute;
  bottom: 100%;
  left: 0%;

  margin-bottom: 6px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.borderGray};
  padding: 5px 10px;
  max-width: 280px;
  z-index: 1;

  // create the arrow
  &:after,
  &:before {
    top: 100%;
    left: 5%;
    border: solid transparent;
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }

  &:after {
    border-color: rgba(255, 255, 255, 0);
    border-top-color: ${({ theme }) => theme.colors.white};
    border-width: 5px;
    margin-left: -5px;
  }
  &:before {
    border-color: rgba(184, 184, 184, 0);
    border-top-color: ${({ theme }) => theme.colors.borderGray};
    border-width: 6px;
    margin-left: -6px;
  }
`;

const StyledErrorMessage = styled('div')<{ wasFailure?: boolean }>`
  color: ${({ wasFailure, theme }) =>
    wasFailure ? theme.colors.gray : theme.colors.black};
  padding: 5px 0;

  img {
    position: relative;
    top: 3px;
  }
`;

export const errorStyle = (props): any =>
  props.error &&
  css`
    border-color: ${props.theme.colors.red};

    &:focus {
      border-color: ${props.theme.colors.red};
    }
  `;

export type FieldProps = {
  id: string;
  label: string;
  pt?: string;
  icon?: ReactNode;
} & FieldRenderProps<any>;

const withPasswordError = (FieldComponent) => {
  const StyledField = styled(FieldComponent)<{ error: boolean }>`
    ${errorStyle}
  `;

  return class extends PureComponent<FieldProps> {
    render() {
      const { id, label, icon, input, meta, pt, ...rest } = this.props;
      const isActive = meta.active;
      const hasRequiredError =
        meta.touched && !!required(input.value) && !meta.submitting;
      const hasPasswordError = Boolean(
        meta.touched &&
          (meta.error || (meta.submitError && !meta.dirtySinceLastSubmit)) &&
          !meta.submitting &&
          !hasRequiredError
      );
      const hasError = hasRequiredError || hasPasswordError;

      const validationRules = [
        {
          message: 'validations.lenghBetween',
          validate: (value) => minLength(8)(value) || maxLength(20)(value),
        },
        {
          message: 'validations.requiredNumber',
          validate: requiredNumber,
        },
        {
          message: 'validations.requiredUppercase',
          validate: requiredUppercase,
        },
        {
          message: 'validations.requiredLowercase',
          validate: requiredLowercase,
        },
        {
          message: 'validations.requiredSpecialCharacter',
          validate: requiredSpecialCharacter,
        },
      ];

      return (
        <StyledBox pt={pt}>
          {isActive && hasPasswordError ? (
            <PasswordError id="passwordError" aria-describedby={id}>
              {validationRules.map((rule) => {
                return (
                  <StyledErrorMessage
                    key={rule.message}
                    wasFailure={!!rule.validate(input.value)}
                  >
                    {rule.validate(input.value) ? (
                      <img
                        src="/static/circle-checkbox-gray.png"
                        alt="checkbox"
                      />
                    ) : (
                      <img
                        src="/static/circle-checkbox-green.png"
                        alt="checkbox"
                      />
                    )}
                    <Text as="span" pl="5px">
                      <FormattedMessage id={rule.message} />
                    </Text>
                  </StyledErrorMessage>
                );
              })}
            </PasswordError>
          ) : null}

          <FormField>
            <Label>{label}</Label>

            <StyledField {...input} error={hasError} id={id} {...rest} />
            {icon || null}
          </FormField>
          {hasRequiredError || (!isActive && hasPasswordError) ? (
            <FieldError id={`${id}Error`} aria-describedby={id}>
              {meta.error && meta.error.id ? (
                <FormattedMessage
                  id={meta.error.id}
                  values={{ ...meta.error.values }}
                />
              ) : (
                meta.error || INVALID_PASSWORD
              )}
            </FieldError>
          ) : null}
        </StyledBox>
      );
    }
  };
};

export default withPasswordError;
